import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../../components/owner/PageHeader';
import {
  ColumnFilterHeader,
  isColumnFilterActive,
  matchTextFilter,
  useColumnFilters,
} from '../../../components/admin/ColumnFilterHeader';
import PackageForm from './PackageForm';
import {
  fetchAllPackages,
  fetchBranchNames,
  savePackage,
  deletePackage,
} from '../../../services/gym/catalogApi';
import { usePanelToast } from '../../../context/PanelToastContext';
import { instantLoadState } from '../../../utils/instantLoad';
import PrintActionsHeader from '../../../components/shared/PrintActionsHeader';
import { matchSaleDateRange } from '../../receptionist/sales/saleHistoryFilters';
import {
  exportPackageCsv,
  exportPackageExcel,
  exportPackagePdf,
  exportPackages,
  printPackageList,
} from './exportPackages';
import '../../owner/ownerPages.css';
import '../../owner/subscriptions/subscriptions.css';
import '../../receptionist/members/members.css';
import '../adminListPages.css';
import './packages.css';

function StatusBadge({ status }) {
  const isActive = status === 'active';
  return (
    <span className={`owner-badge ${isActive ? 'owner-badge-success' : 'owner-badge-warning'}`}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

function PackageManagement() {
  const toast = usePanelToast();
  const packageBootstrap = instantLoadState('packages:all:all');
  const [packageList, setPackageList] = useState(packageBootstrap.initialData);
  const [branchOptions, setBranchOptions] = useState(() => instantLoadState('branches').initialData.map((b) => b.name).filter(Boolean));
  const [loading, setLoading] = useState(packageBootstrap.initialLoading);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters({
    name: '',
    serialNo: '',
    branch: '',
    members: '',
    status: '',
  });

  const loadData = useCallback(async () => {
    try {
      setError('');
      if (!packageList.length) setLoading(true);
      const [packages, branches] = await Promise.all([
        fetchAllPackages(),
        fetchBranchNames(),
      ]);
      setPackageList(packages);
      setBranchOptions(branches);
    } catch (err) {
      setError(err.message || 'Failed to load packages');
    } finally {
      setLoading(false);
    }
  }, [packageList.length]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const hasActiveFilters = useMemo(() => (
    isColumnFilterActive(filters, 'name')
    || isColumnFilterActive(filters, 'serialNo')
    || isColumnFilterActive(filters, 'branch')
    || isColumnFilterActive(filters, 'members')
    || isColumnFilterActive(filters, 'status')
  ), [filters]);

  const filteredPackages = useMemo(() => (
    packageList.filter((pkg) => {
      const rowDate = pkg.createdAt || pkg.created_at || pkg.date || '';
      const dateOk = !rowDate || matchSaleDateRange(rowDate, rangeStart, rangeEnd);
      return (
        dateOk
        && matchTextFilter(pkg.name, filters.name)
        && matchTextFilter(
          pkg.serialNo !== '' && pkg.serialNo != null ? `ID-${pkg.serialNo}` : '—',
          filters.serialNo,
        )
        && matchTextFilter(pkg.branch || '—', filters.branch)
        && matchTextFilter(String(pkg.members || 0), filters.members)
        && matchTextFilter(pkg.status === 'active' ? 'Active' : 'Inactive', filters.status)
      );
    })
  ), [packageList, filters, rangeStart, rangeEnd]);

  const openCreateModal = () => {
    setFormKey((k) => k + 1);
    setShowCreateModal(true);
  };

  const closeCreateModal = () => setShowCreateModal(false);

  const handleCreateSubmit = async (form) => {
    try {
      setError('');
      const created = await savePackage(form);
      await loadData();
      // Prefer freshest list; ensure created row appears with serial
      setPackageList((prev) => {
        const without = prev.filter((p) => p.id !== created.id);
        return [created, ...without];
      });
      closeCreateModal();
      toast.showAdded('Package');
    } catch (err) {
      setError(err.message || 'Failed to create package');
      toast.showError(err.message || 'Failed to create package');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deletePackage(deleteTarget.id);
      setPackageList((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      setDeleteTarget(null);
      toast.showDeleted('Package');
    } catch (err) {
      setError(err.message || 'Failed to delete package');
    }
  };

  return (
    <div>
      <PageHeader title="Packages" subtitle="Create and manage membership plans">
        <button type="button" className="owner-btn owner-btn-primary" onClick={openCreateModal}>
          Create Package
        </button>
      </PageHeader>

      <PrintActionsHeader
        startDate={rangeStart}
        endDate={rangeEnd}
        onStartDateChange={setRangeStart}
        onEndDateChange={setRangeEnd}
        onClearDates={() => { setRangeStart(''); setRangeEnd(''); }}
        onPrint={() => printPackageList(filteredPackages)}
        onCsv={() => exportPackageCsv(filteredPackages)}
        onPdf={() => exportPackagePdf(filteredPackages)}
        onExcel={() => exportPackageExcel(filteredPackages)}
        onExport={() => exportPackages(filteredPackages)}
        ariaLabel="Package print and export"
      />

      {error && <p className="owner-placeholder-text owner-mb-16">{error}</p>}
      {loading && <p className="owner-placeholder-text owner-mb-16">Loading packages...</p>}

      <div className="owner-card rec-panel-card rec-member-list-card">
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Package"
                  filterKey="name"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search package..."
                />
                <ColumnFilterHeader
                  label="Serial No"
                  filterKey="serialNo"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search serial..."
                />
                <ColumnFilterHeader
                  label="Branch"
                  filterKey="branch"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search branch..."
                />
                <ColumnFilterHeader
                  label="Members"
                  filterKey="members"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search members..."
                />
                <ColumnFilterHeader
                  label="Status"
                  filterKey="status"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search status..."
                />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPackages.length > 0 ? (
                filteredPackages.map((pkg) => (
                  <tr key={pkg.id}>
                    <td>{pkg.name}</td>
                    <td>{pkg.serialNo !== '' && pkg.serialNo != null ? `ID-${pkg.serialNo}` : '—'}</td>
                    <td>{pkg.branch || '—'}</td>
                    <td>{pkg.members || 0}</td>
                    <td><StatusBadge status={pkg.status} /></td>
                    <td>
                      <div className="rec-member-actions-cell">
                        <Link to={`/admin/packages/edit/${pkg.id}`} className="owner-btn owner-btn-secondary owner-btn-sm">
                          Edit
                        </Link>
                        <button
                          type="button"
                          className="owner-btn owner-btn-secondary owner-btn-sm"
                          onClick={() => setDeleteTarget(pkg)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="owner-placeholder-text">
                    {hasActiveFilters ? 'No packages match your filters.' : 'No packages yet. Create one to show in the receptionist panel.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showCreateModal && (
        <div className="subscription-modal-overlay" onClick={closeCreateModal}>
          <div className="subscription-modal admin-form-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Create Package</h2>
              <button type="button" className="subscription-modal-close" onClick={closeCreateModal} aria-label="Close">×</button>
            </div>
            <PackageForm
              key={formKey}
              branchOptions={branchOptions}
              submitLabel="Create Package"
              onSubmit={handleCreateSubmit}
              onCancel={closeCreateModal}
            />
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="subscription-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="subscription-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Delete Package</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setDeleteTarget(null)} aria-label="Close">×</button>
            </div>
            <div className="subscription-form">
              <p className="membership-modal-message">
                Are you sure you want to delete <strong>{deleteTarget.name}</strong>?
              </p>
              <div className="subscription-form-actions">
                <button type="button" className="owner-btn membership-btn-danger" onClick={handleDelete}>Delete</button>
                <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PackageManagement;
